/**
 * Créer une gamme, ou modifier son identité : libellé, modèles de machine pour lesquels on la
 * propose (critères), références constructeur pour lesquelles ses pièces ont été établies.
 *
 * TROIS USAGES :
 * - création d'une gamme vide ;
 * - modification de l'identité d'une gamme existante ;
 * - COPIE d'une autre gamme (`copie`) : le contenu de la version choisie — paliers, lignes, prix — est
 *   copié côté serveur en version 1 brouillon de la nouvelle gamme. Les champs arrivent préremplis
 *   (d'après la gamme, ou d'après une machine) et la marque est celle de l'originale.
 *
 * Ce panneau ne touche JAMAIS au contenu d'une gamme (paliers, lignes, prix) : ce contenu vit dans
 * ses versions, publiées et immuables. La marque et le code se choisissent à la création et ne
 * changent plus.
 */
import { useEffect, useMemo, useState } from 'react';

import {
  ApiError,
  api,
  type GammeDetail,
  type GammeResume,
  type GammeVersion,
  type Party,
} from '../api/client';
import {
  codeDeCopie,
  criteresPourEnvoi,
  ligneCritereDe,
  ligneCritereVide,
  lignesDepuisGamme,
  lireReferences,
  texteReferences,
  validerIdentite,
  type LigneCritere,
} from '../domaine/gammes-identite';
import { ComboRecherche, type OptionCombo } from './ComboRecherche';
import { Champ, ErreurFormulaire, Panneau } from './Formulaire';

/** Une copie de gamme : d'où l'on part, et ce qui remplace les critères et références de l'originale. */
export interface CopieGamme {
  readonly depuis: GammeDetail;
  /** La version dont le contenu est copié. */
  readonly version: GammeVersion;
  /** Critères préremplis (par exemple le modèle et la référence d'une machine) ; sinon ceux de l'originale. */
  readonly criteres?: readonly { readonly modele: string; readonly reference: string }[];
  /** Références constructeur préremplies ; sinon celles de l'originale. */
  readonly references?: readonly string[];
}

export function FormulaireGamme({
  gamme,
  copie,
  onFermer,
  onEnregistree,
}: {
  /** Modification de cette gamme. Absente : création (vide, ou copie si `copie` est donnée). */
  gamme?: GammeResume;
  copie?: CopieGamme;
  onFermer: () => void;
  onEnregistree: (gamme: GammeResume) => void;
}) {
  const edition = gamme !== undefined;

  const [libelle, setLibelle] = useState(gamme?.label ?? copie?.depuis.label ?? '');
  const [code, setCode] = useState(copie ? codeDeCopie(copie.depuis.code) : '');
  const [marqueId, setMarqueId] = useState<string | null>(copie?.depuis.brandPartyId ?? null);
  const [marques, setMarques] = useState<Party[]>([]);
  const [lignes, setLignes] = useState<LigneCritere[]>(() => {
    if (gamme && gamme.criteres.length > 0) return lignesDepuisGamme(gamme.criteres);
    if (copie?.criteres && copie.criteres.length > 0) {
      return copie.criteres.map((c) => ligneCritereDe(c.modele, c.reference));
    }
    if (copie && copie.depuis.criteres.length > 0) return lignesDepuisGamme(copie.depuis.criteres);
    return [ligneCritereVide()];
  });
  const [references, setReferences] = useState(
    texteReferences(gamme?.referencesConstructeur ?? copie?.references ?? copie?.depuis.referencesConstructeur ?? []),
  );
  const [erreur, setErreur] = useState<ApiError | Error | null>(null);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    // La marque ne se choisit qu'à la création d'une gamme vide (une copie garde celle de l'originale) ;
    // la liste se charge sans bloquer le reste du formulaire.
    if (!edition && !copie) void api.parties(undefined, 'BRAND').then(setMarques).catch(() => undefined);
  }, [edition, copie]);

  const optionsMarque = useMemo<OptionCombo[]>(
    () => marques.map((m) => ({ id: m.id, label: m.legalName })),
    [marques],
  );

  const majLigne = (id: number, patch: Partial<Pick<LigneCritere, 'modele' | 'reference'>>) =>
    setLignes((ls) => ls.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  const retirerLigne = (id: number) => setLignes((ls) => ls.filter((l) => l.id !== id));

  const enregistrer = async () => {
    const defaut = validerIdentite({
      libelle,
      ...(edition ? {} : { marqueId }),
      lignes,
      ...(copie ? { libelleOriginal: copie.depuis.label } : {}),
    });
    if (defaut) {
      setErreur(new Error(defaut));
      return;
    }

    setEnCours(true);
    setErreur(null);
    try {
      const criteres = criteresPourEnvoi(lignes);
      const refs = lireReferences(references);
      let rendue: GammeResume;
      if (gamme) {
        rendue = await api.updateMaintenanceProfile(gamme.id, {
          label: libelle.trim(),
          referencesConstructeur: refs,
          criteres,
        });
      } else {
        if (!marqueId) return;
        rendue = await api.createMaintenanceProfile({
          ...(code.trim() ? { code: code.trim() } : {}),
          label: libelle.trim(),
          brandPartyId: marqueId,
          criteres,
          referencesConstructeur: refs,
          ...(copie ? { depuisVersionId: copie.version.id } : {}),
        });
      }
      onEnregistree(rendue);
    } catch (e) {
      setErreur(e instanceof ApiError || e instanceof Error ? e : new Error('Enregistrement impossible.'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Panneau
      titre={
        edition ? 'Modifier la gamme' : copie ? `Nouvelle gamme à partir de ${copie.depuis.code}` : 'Nouvelle gamme'
      }
      sousTitre={
        edition
          ? 'Libellé, modèles et références. Les pièces et les prix se règlent dans les versions.'
          : copie
            ? `Copie de la version ${copie.version.versionNumber} de « ${copie.depuis.label} » : paliers, lignes et prix repris tels quels, dans un brouillon. Rien ne s’applique aux machines tant qu’il n’est pas publié.`
            : 'Une gamme se crée vide : ses pièces s’ajoutent ensuite, dans une première version.'
      }
      onFermer={onFermer}
      actions={
        <>
          <button className="secondaire" onClick={onFermer} disabled={enCours}>
            Annuler
          </button>
          <button onClick={() => void enregistrer()} disabled={enCours}>
            {enCours ? 'Enregistrement…' : edition ? 'Enregistrer' : 'Créer la gamme'}
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void enregistrer();
        }}
      >
        <ErreurFormulaire
          message={erreur?.message ?? null}
          details={erreur instanceof ApiError ? erreur.details : undefined}
        />

        <Champ libelle="Libellé" requis aide="Ce que l’écran affiche : « KAESER DSD240 · réf. 101511.0 ».">
          <input
            type="text"
            autoFocus
            placeholder="KAESER SK24"
            value={libelle}
            onChange={(e) => setLibelle(e.target.value)}
          />
        </Champ>

        {edition ? (
          <p className="aide">
            Code : <code>{gamme.code}</code> · marque : <strong>{gamme.brandName}</strong>. Ni l’un ni
            l’autre ne change après la création.
          </p>
        ) : (
          <>
            {copie ? (
              <p className="aide">
                Marque : <strong>{copie.depuis.brandName}</strong> — celle de la gamme d’origine : une référence
                appartient à son constructeur.
              </p>
            ) : (
              <Champ libelle="Marque" requis aide="Le constructeur : c’est lui qui porte les références des pièces.">
                <ComboRecherche
                  options={optionsMarque}
                  valeur={marqueId}
                  onChange={setMarqueId}
                  placeholder="Chercher une marque…"
                  viderLibelle="Aucune marque choisie"
                />
              </Champ>
            )}

            <Champ
              libelle="Code"
              aide={
                copie
                  ? 'Proposé d’après celui de l’originale ; changez-le si vous voulez. Majuscules, chiffres et « _ » ; il ne change plus ensuite.'
                  : 'Facultatif. Laissé vide, il est déduit de la marque et du premier modèle (ex. KAESER_SK24). Majuscules, chiffres et « _ » ; il ne change plus ensuite.'
              }
            >
              <input
                type="text"
                placeholder="KAESER_SK24"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </Champ>
          </>
        )}

        <Champ
          libelle="Modèles de machine"
          requis
          aide="La gamme est PROPOSÉE à toute machine de cette marque dont le modèle correspond (« DD 35+ » et « dd35+ » sont le même modèle). La référence constructeur est facultative : elle restreint la proposition à cette variante."
        >
          <div className="lignes-criteres">
            {lignes.map((l) => (
              <div key={l.id} className="saisie-ligne">
                <input
                  type="text"
                  aria-label="Modèle"
                  placeholder="Modèle (ex. DD35+)"
                  value={l.modele}
                  onChange={(e) => majLigne(l.id, { modele: e.target.value })}
                />
                <input
                  type="text"
                  aria-label="Référence constructeur"
                  placeholder="Référence (facultatif)"
                  value={l.reference}
                  onChange={(e) => majLigne(l.id, { reference: e.target.value })}
                />
                {lignes.length > 1 && (
                  <button
                    type="button"
                    className="secondaire discret"
                    aria-label="Retirer ce modèle"
                    onClick={() => retirerLigne(l.id)}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              className="secondaire discret"
              onClick={() => setLignes((ls) => [...ls, ligneCritereVide()])}
            >
              + Ajouter un modèle
            </button>
          </div>
        </Champ>

        <Champ
          libelle="Références constructeur de la gamme"
          aide="Une par ligne. À renseigner seulement si les pièces ont été relevées pour une variante précise (ex. 101511.0). Sert à AVERTIR quand une machine porte une autre référence, jamais à la cacher."
        >
          <textarea
            rows={3}
            placeholder="101511.0"
            value={references}
            onChange={(e) => setReferences(e.target.value)}
          />
        </Champ>

        {edition && (
          <div className="message info">
            Retirer un modèle ne détache aucune machine : un rattachement est explicite et ne dépend pas
            des critères. Le modèle retiré cesse seulement d’être proposé.
          </div>
        )}
      </form>
    </Panneau>
  );
}
